import { useEffect, useState } from "react";
import { useAuth } from "../../../store/authEtablissementStore";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import StatusBadge from "../../../components/identity-verification/StatusBadge";
import DocumentUploadField from "../../../components/identity-verification/DocumentUploadField";
import {
  getEtablissementIdentityVerification,
  submitEtablissementIdentityVerification,
  type EtablissementIdentityVerification,
} from "../../../api/identityVerification";

export default function EtablissementIdentityVerificationPage() {
  const { establishment } = useAuth();
  const id = establishment?.id?.trim() ?? "";
  const [state, setState] = useState<EtablissementIdentityVerification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    setLoading(true);
    getEtablissementIdentityVerification(id).then((res) => {
      if (!mounted) return;
      if (res.ok) setState(res.data);
      else setError(res.message);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, [id]);

  const submit = async (body: { carteCipResponsableUrl?: string; arreteMinisterielUrl?: string }) => {
    if (!id) return { ok: false as const, message: "Session invalide." };
    const res = await submitEtablissementIdentityVerification(id, body);
    if (!res.ok) return { ok: false as const, message: res.message };
    setState(res.data);
    return { ok: true as const };
  };

  const missing =
    state && state.statut === "non_soumis"
      ? [!state.carteCipResponsableUrl && "carte CIP du responsable", !state.arreteMinisterielUrl && "arrêté ministériel"]
          .filter(Boolean)
          .join(" et ")
      : "";

  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0B1F16]">Vérification d&apos;identité</h1>
      <p className="mt-2 text-sm text-gray-600">
        Soumettez la carte CIP du responsable et l&apos;arrêté ministériel d&apos;autorisation d&apos;ouverture et
        d&apos;exploitation.
      </p>

      <Card className="mt-6">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Documents justificatifs</CardTitle>
          {state && <StatusBadge status={state.statut} />}
        </CardHeader>
        <CardContent>
          {loading && <p className="text-sm text-gray-500">Chargement…</p>}
          {error && !loading && <p className="text-sm text-red-600">{error}</p>}

          {!loading && state && (
            <div className="space-y-6">
              {state.statut === "rejetee" && state.motifRejet && (
                <div className="rounded-[10px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  <span className="font-semibold">Motif du rejet : </span>
                  {state.motifRejet}
                </div>
              )}
              {missing && (
                <p className="text-sm text-amber-700">Document(s) manquant(s) : {missing}.</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DocumentUploadField
                  label="Carte CIP du responsable"
                  folderName="identity-verification/etablissements"
                  filename={id ? `carte_cip_responsable_${id}` : undefined}
                  currentUrl={state.carteCipResponsableUrl}
                  readOnly={state.statut === "validee"}
                  onSubmitted={(url) => submit({ carteCipResponsableUrl: url })}
                />
                <DocumentUploadField
                  label="Arrêté ministériel"
                  folderName="identity-verification/etablissements"
                  filename={id ? `arrete_ministeriel_${id}` : undefined}
                  currentUrl={state.arreteMinisterielUrl}
                  readOnly={state.statut === "validee"}
                  onSubmitted={(url) => submit({ arreteMinisterielUrl: url })}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
